import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Button from '../components/common/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { apiService } from '../services/api';
import { createPaymentOrder } from '../services/payments';
import { Product } from '../types';

// Custom formatter for RSD
const formatRSD = (amount: number): string => {
  return new Intl.NumberFormat('sr-RS', {
    style: 'currency',
    currency: 'RSD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const TokenPurchasePage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadProducts = async () => {
      setIsLoadingProducts(true);
      setHasLoadError(false);
      try {
        const response = await apiService.getProducts();
        if (isMounted) {
          setProducts(response.items);
        }
      } catch (_error) {
        if (isMounted) {
          setHasLoadError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoadingProducts(false);
        }
      }
    };

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedProduct = products.find((product) => product.id === selectedPackage);
  const productDescription = (product: Product) => {
    if (language === 'sr') {
      return product.descriptionSr || product.descriptionEn;
    }
    return product.descriptionEn || product.descriptionSr;
  };
  
  const handlePurchase = async () => {
    if (!selectedPackage) return;
    if (!user) {
      const redirectTo = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`);
      return;
    }

    setPurchaseError(null);
    setIsConfirmOpen(true);
  };

  const handleConfirmPurchase = async () => {
    if (!selectedProduct) return;
    if (!user) {
      const redirectTo = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`);
      return;
    }

    setIsCreatingOrder(true);
    setPurchaseError(null);
    try {
      const order = await createPaymentOrder(selectedProduct.id);
      setIsConfirmOpen(false);
      navigate(`/pay-redirect/${order.orderId}`);
    } catch (error) {
      setPurchaseError(t('tokens.paymentInitError'));
    } finally {
      setIsCreatingOrder(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('tokens.purchaseTitle')}</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('tokens.description')}
          </p>
        </div>
        
        {user && (
          <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
            <p className="text-lg text-gray-900">
              {t('tokens.currentBalance')}: <span className="font-bold">{user.tokens} {t('common.tokens')}</span>
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {isLoadingProducts && (
            <div className="col-span-full flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}
          {!isLoadingProducts && hasLoadError && (
            <div className="col-span-full text-center text-red-600">
              {t('tokens.productsLoadError')}
            </div>
          )}
          {!isLoadingProducts && !hasLoadError && products.length === 0 && (
            <div className="col-span-full text-center text-gray-500">
              {t('tokens.noProducts')}
            </div>
          )}
          {!isLoadingProducts && !hasLoadError && products.map((product) => (
            <div 
              key={product.id}
              className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 transform hover:shadow-md ${
                selectedPackage === product.id ? 'ring-2 ring-primary-500 scale-105' : ''
              } ${product.popular ? 'relative' : ''}`}
            >
              {product.popular && (
                <div className="absolute top-0 right-0 bg-brand-dark text-white text-xs font-bold px-3 py-1">
                  {t('tokens.mostPopular')}
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t(`tokens.packages.${product.name.toLowerCase()}.name`)}</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl font-bold text-brand-dark">{formatRSD(product.price)}</span>
                  <span className="ml-2 text-gray-500">{t('tokens.oneTime')}</span>
                </div>
                <div className="bg-primary-50 rounded-lg p-3 mb-4">
                  <p className="text-primary-700 font-medium text-center">
                    {product.tokens} {t('common.tokens')}
                  </p>
                </div>
                <p className="text-gray-600 mb-6">{productDescription(product)}</p>
                <Button 
                  variant={selectedPackage === product.id ? 'primary' : 'outline'} 
                  fullWidth
                  onClick={() => setSelectedPackage(product.id)}
                >
                  {selectedPackage === product.id ? t('tokens.selected') : t('tokens.selectPackage')}
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Payment Section */}
        <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('tokens.paymentInfo')}</h2>
          
          <div className="space-y-4 mb-6">
            <p className="text-gray-600">
              {t('tokens.demoNotice')}
            </p>
            
            <div className="border border-gray-200 rounded-lg p-4">
              {selectedPackage && selectedProduct ? (
                <>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium text-gray-700">
                      {t(`tokens.packages.${selectedProduct.name.toLowerCase()}.name`)}
                    </span>
                    <span className="font-bold text-gray-900">
                      {formatRSD(selectedProduct.price)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{t('common.tokens')}</span>
                    <span>{selectedProduct.tokens}</span>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-center">{t('tokens.selectPackageFirst')}</p>
              )}
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button 
              onClick={handlePurchase}
              disabled={!selectedPackage}
              className="min-w-[150px]"
            >
              {t('tokens.completePurchase')}
            </Button>
          </div>
        </div>

        {isConfirmOpen && selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('tokens.confirmPurchaseTitle')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('tokens.confirmPurchaseDescription')}
              </p>

              <div className="border border-gray-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-700">
                  <span className="font-medium">
                    {t(`tokens.packages.${selectedProduct.name.toLowerCase()}.name`)}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {formatRSD(selectedProduct.price)}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('common.tokens')}</span>
                  <span>{selectedProduct.tokens}</span>
                </div>
              </div>

              {purchaseError && (
                <p className="text-sm text-red-600 mt-3">{purchaseError}</p>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setIsConfirmOpen(false)}
                  disabled={isCreatingOrder}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleConfirmPurchase}
                  loading={isCreatingOrder}
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* FAQ Section */}
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('tokens.faq.title')}</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('tokens.faq.whatAreTokens.title')}</h3>
              <p className="text-gray-600">
                {t('tokens.faq.whatAreTokens.answer')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('tokens.faq.expiration.title')}</h3>
              <p className="text-gray-600">
                {t('tokens.faq.expiration.answer')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('tokens.faq.refunds.title')}</h3>
              <p className="text-gray-600">
                {t('tokens.faq.refunds.answer')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('tokens.faq.howToUse.title')}</h3>
              <p className="text-gray-600">
                {t('tokens.faq.howToUse.answer')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenPurchasePage;
